export const LAYERING_FEEDBACK_OPTIONS = Object.freeze([
  Object.freeze({ value: 'too_sweet', label: 'Too sweet' }),
  Object.freeze({ value: 'perfect', label: 'Perfect' }),
  Object.freeze({ value: 'base_overpowered', label: 'Base overpowered' }),
  Object.freeze({ value: 'vanished', label: 'Vanished' }),
  Object.freeze({ value: 'clashed', label: 'Clashed' }),
])

const FEEDBACK_VALUES = new Set(LAYERING_FEEDBACK_OPTIONS.map(option => option.value))
const FEEDBACK_WEIGHT = Object.freeze({
  too_sweet: -1,
  perfect: 2,
  base_overpowered: -1,
  vanished: -1,
  clashed: -1,
})

function normalized(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function titleCase(value) {
  const clean = String(value || '').trim()
  return clean ? clean[0].toLocaleUpperCase() + clean.slice(1) : ''
}

export function pairingIdentity(pairing) {
  const catalogId = String(pairing?.catalog_id || '').trim()
  if (catalogId) return `catalog:${catalogId}`
  const label = normalized(`${pairing?.house || ''} ${pairing?.name || ''}`)
  return label ? `label:${label}` : ''
}

export function pairingLabel(pairing) {
  return normalized(`${pairing?.house || ''} ${pairing?.name || ''}`)
}

export function findSuggestedPairingForWear(wear, pairingRows = []) {
  const layeredLabels = new Set((wear?.layered_with || []).map(normalized).filter(Boolean))
  if (!wear?.fragrance_id || layeredLabels.size === 0) return null

  for (const pairingRow of pairingRows) {
    if (pairingRow?.fragrance_id !== wear.fragrance_id) continue
    const cards = Array.isArray(pairingRow?.suggested_pairings) ? pairingRow.suggested_pairings : []
    const pairing = cards.find(card => layeredLabels.has(pairingLabel(card)))
    if (pairing) return { pairingRow, pairing }
  }
  return null
}

export function annotatePairingFeedback(cards, targetPairing, value, feedbackAt, volatilityBridgeNote = null) {
  const targetIdentity = pairingIdentity(targetPairing)
  return (Array.isArray(cards) ? cards : []).map(card => (
    targetIdentity && pairingIdentity(card) === targetIdentity
      ? {
          ...card,
          user_feedback: value,
          user_feedback_at: feedbackAt,
          user_feedback_history: [
            ...(Array.isArray(card.user_feedback_history) ? card.user_feedback_history.slice(-49) : []),
            { value, at: feedbackAt },
          ],
          volatility_bridge_note: volatilityBridgeNote,
        }
      : card
  ))
}

export function preservePairingFeedback(cards = [], previousCards = []) {
  const previousByIdentity = new Map((Array.isArray(previousCards) ? previousCards : [])
    .map(card => [pairingIdentity(card), card])
    .filter(([identity]) => identity))
  return (Array.isArray(cards) ? cards : []).map(card => {
    const previous = previousByIdentity.get(pairingIdentity(card))
    if (!previous) return card
    return {
      ...card,
      ...(['user_feedback', 'user_feedback_at', 'user_feedback_history', 'volatility_bridge_note']
        .reduce((preserved, key) => (
          previous[key] === undefined ? preserved : { ...preserved, [key]: previous[key] }
        ), {})),
    }
  })
}

export function buildUserLayeringBias(pairingRows = []) {
  const counts = Object.fromEntries(LAYERING_FEEDBACK_OPTIONS.map(option => [option.value, 0]))
  const scoreByPairing = {}

  for (const row of pairingRows) {
    let annotatedFeedbackCount = 0
    for (const card of Array.isArray(row?.suggested_pairings) ? row.suggested_pairings : []) {
      const identity = pairingIdentity(card)
      if (!identity) continue
      const history = Array.isArray(card?.user_feedback_history) && card.user_feedback_history.length
        ? card.user_feedback_history
        : FEEDBACK_VALUES.has(card?.user_feedback) ? [{ value: card.user_feedback }] : []
      for (const event of history) {
        if (!FEEDBACK_VALUES.has(event?.value)) continue
        annotatedFeedbackCount += 1
        counts[event.value] += 1
        scoreByPairing[identity] = (scoreByPairing[identity] || 0) + FEEDBACK_WEIGHT[event.value]
      }
    }
    // Phase 1 rows written before per-card annotations retain a useful
    // aggregate outcome, but never apply it to an unknown suggestion.
    if (annotatedFeedbackCount === 0 && FEEDBACK_VALUES.has(row?.feedback)) counts[row.feedback] += 1
  }

  return { counts, scoreByPairing }
}

export function rerankPairingSuggestions(cards = [], bias = {}) {
  return (Array.isArray(cards) ? cards : [])
    .map((card, index) => ({
      card,
      index,
      bias: Number(bias?.scoreByPairing?.[pairingIdentity(card)] || 0),
    }))
    .sort((left, right) => right.bias - left.bias || left.index - right.index)
    .map(item => item.card)
}

function numeric(value) {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function notes(value) {
  if (Array.isArray(value)) return value.map(item => String(item || '').trim()).filter(Boolean)
  if (typeof value !== 'string') return []
  return value.split(/[,|]/).map(item => item.trim()).filter(Boolean)
}

export function buildVolatilityBridgeNote({
  anchorCatalogId,
  partnerCatalogId,
  volatilityRows = [],
  catalogRows = [],
}) {
  const volatilityById = new Map(volatilityRows.map(row => [String(row.fragrance_id), row]))
  const catalogById = new Map(catalogRows.map(row => [String(row.id), row]))
  const anchorVolatility = volatilityById.get(String(anchorCatalogId))
  const partnerVolatility = volatilityById.get(String(partnerCatalogId))

  // Both sides must clear the evidence threshold. A one-sided explanation
  // would imply knowledge about the uncovered half of the composition.
  if (numeric(anchorVolatility?.coverage_ratio) < 0.6
      || numeric(partnerVolatility?.coverage_ratio) < 0.6) return null

  const anchor = catalogById.get(String(anchorCatalogId))
  const partner = catalogById.get(String(partnerCatalogId))
  if (!anchor || !partner) return null

  const anchorTop = numeric(anchorVolatility?.top_volatility)
  const partnerTop = numeric(partnerVolatility?.top_volatility)
  if (anchorTop === null && partnerTop === null) return null

  const anchorIsLifter = partnerTop === null || (anchorTop !== null && anchorTop >= partnerTop)
  const lifter = anchorIsLifter ? anchor : partner
  const foundation = anchorIsLifter ? partner : anchor
  const bridgeNote = notes(lifter.top_notes)[0]
  if (!bridgeNote || !lifter.name || !foundation.name) return null

  return `${titleCase(bridgeNote)} in ${lifter.name} lifts the ${foundation.name} base so it does not sit flat.`
}

export async function loadVolatilityBridgeNote(client, {
  anchorCatalogId,
  partnerCatalogId,
}) {
  if (!anchorCatalogId || !partnerCatalogId) return null
  const ids = [...new Set([String(anchorCatalogId), String(partnerCatalogId)])]
  const [{ data: volatilityRows, error: volatilityError }, { data: catalogRows, error: catalogError }] = await Promise.all([
    client
      .schema('public')
      .from('fragrance_volatility_v1')
      .select('fragrance_id,top_volatility,base_volatility,coverage_ratio')
      .in('fragrance_id', ids),
    client
      .from('catalog')
      .select('id,name,house,top_notes,base_notes')
      .in('id', ids),
  ])
  if (volatilityError) throw volatilityError
  if (catalogError) throw catalogError
  return buildVolatilityBridgeNote({
    anchorCatalogId,
    partnerCatalogId,
    volatilityRows,
    catalogRows,
  })
}

export async function writePairingFeedback(client, {
  pairingRow,
  pairing,
  userId,
  value,
  volatilityBridgeNote = null,
  at = new Date(),
}) {
  if (!FEEDBACK_VALUES.has(value)) throw new Error('Unsupported layering feedback value.')
  if (!pairingRow?.id || !pairingRow?.fragrance_id || !userId) {
    throw new Error('Layering feedback requires an owner-scoped pairing row.')
  }
  const feedbackAt = at instanceof Date ? at.toISOString() : new Date(at).toISOString()
  const suggestedPairings = annotatePairingFeedback(
    pairingRow.suggested_pairings,
    pairing,
    value,
    feedbackAt,
    volatilityBridgeNote,
  )
  const { data, error } = await client
    .from('pairings')
    .update({
      feedback: value,
      feedback_at: feedbackAt,
      volatility_bridge_note: volatilityBridgeNote,
      suggested_pairings: suggestedPairings,
    })
    .eq('id', pairingRow.id)
    .eq('fragrance_id', pairingRow.fragrance_id)
    .eq('user_id', userId)
    .select('id,fragrance_id,feedback,feedback_at,volatility_bridge_note,suggested_pairings')
    .single()
  if (error) throw error
  return data
}
